import { useNavigate } from "react-router-dom";
import { useToast } from "@/shared/hooks/use-toast";
import PresetService, {
  GalleryItem,
} from "@/features/packs/services/presets/presetService";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import { TagsService } from "@/features/tags/service/tagService";

export const usePresetSubmission = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const createPresetMutation = useCustomMutation({
    serviceInstance: PresetService.Instance,
    selectAction: (service) => service.createPreset,
  });
  const updatePresetMutation = useCustomMutation({
    serviceInstance: PresetService.Instance,
    selectAction: (service) => service.updatePreset,
  });

  // Mutations para vincular/desvincular tags
  const linkTagMutation = useCustomMutation({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.linkTagToPreset,
   
  });

  const deletePresetMutation = useCustomMutation({
    serviceInstance: PresetService.Instance,
    selectAction: (service) => service.removePreset,
  });

  const handleSubmit = async (
    isFree: boolean,
    isEditing: boolean,
    packId: string,
    presetId: string | null,
    name: string,
    description: string,
    tags: string,
    coverFile: File | null = null,
    lutFile: File | null = null,
    published: boolean,
    uploadGalleryImages: (presetId: string) => Promise<GalleryItem[]>
  ):Promise<string | number> => {
    try {
      // Validate required fields
      if (!packId) {
        toast({
          title: "Erro de validação",
          description: "ID do pack não encontrado.",
          variant: "destructive",
        });
        return 0;
      }

      if (!name) {
        toast({
          title: "Erro de validação",
          description: "Nome do preset é obrigatório.",
          variant: "destructive",
        });
        return 0;
      }

      // Validação dos arquivos: obrigatórios apenas na criação
      if (!isEditing) {
        if (!coverFile || !(coverFile instanceof File)) {
          toast({
            title: "Arquivo de capa obrigatório",
            description: "Selecione uma imagem de capa válida para o preset.",
            variant: "destructive",
          });
          return 0;
        }
        if (!lutFile || !(lutFile instanceof File)) {
          toast({
            title: "Arquivo LUT obrigatório",
            description:
              "Selecione um arquivo LUT válido (.cube, .3dl, .lut, .jpg, .jpeg)",
            variant: "destructive",
          });
          return 0;
        }
      }

      if (isEditing && presetId) {
        const payload = {
          packId,
          name,
          isFree,
          isBlackAndWhite: false,
          cover: coverFile, // arquivo
          lut: lutFile, // arquivo
          published,
          translations: [{ languageCode: "PT_BR", about: description }],
        };
        try {
          await updatePresetMutation.mutateAsync({
            id: presetId,
            data: payload,
          });
          await uploadGalleryImages(presetId);
        } catch (error) {
          toast({
            title: "Erro ao atualizar preset",
            description:
              error instanceof Error
                ? error.message
                : "Ocorreu um erro durante a atualização. Por favor, tente novamente.",
            variant: "destructive",
          });
          console.error("Error updating preset:", error);
          return 0;
        }

        toast({
          title: "Preset salvo",
          description: "O preset foi atualizado com sucesso.",
        });

        // Add a specific toast for publication status
        if (published) {
          toast({
            title: "Preset publicado!",
            description: "O preset agora está disponível para os usuários.",
          });
        }
        toast({
          title: "Preset Atualizado!",
          description: "Os dados do preset foram atualizados",
        });
        return presetId || 0;
      } else {
        // Create new preset
        const payload = {
          packId,
          name,
          isFree,
          isBlackAndWhite: false,
          cover: coverFile, // arquivo
          lut: lutFile, // arquivo
          published,
          translations: [{ languageCode: "PT_BR", about: description }],
        };
        let newPresetId: string | undefined;
        try {
          const data = await createPresetMutation.mutateAsync({
            data: payload,
          });
          newPresetId = data?.id;
        } catch (error) {
          toast({
            title: "Erro ao criar preset",
            description:
              error instanceof Error
                ? error.message
                : "Ocorreu um erro durante a criação. Por favor, tente novamente.",
            variant: "destructive",
          });
          console.error("Error creating preset:", error);
          return 0;
        }

        // Retorna o ID do preset criado para uso posterior
        if (!newPresetId) {
          toast({
            title: "Erro",
            description: "Preset criado mas ID não foi retornado.",
            variant: "destructive",
          });
          return 0;
        }
        // Upload gallery images (mantém se necessário)
        if (newPresetId) {
          await uploadGalleryImages(newPresetId);
          
          // Vincular tags ao preset recém-criado
          if (tags && tags.trim()) {
            try {
              const tagIds = tags.split(',').map(tag => tag.trim()).filter(Boolean);
              console.log('Vinculando tags ao preset criado:', { newPresetId, tagIds });
              
              for (const tagId of tagIds) {
                await linkTagMutation.mutateAsync({
                  tagId,
                  presetId: newPresetId
                });
                console.log('Tag vinculada com sucesso:', tagId);
              }
              
              console.log('Todas as tags foram vinculadas ao preset');
            } catch (error) {
              console.error('Erro ao vincular tags ao preset:', error);
              toast({
                title: "Aviso",
                description: "Preset criado, mas houve erro ao vincular algumas tags.",
                variant: "destructive",
              });
            }
          }
        }

        toast({
          title: "Preset salvo",
          description: "O preset foi adicionado com sucesso ao pack.",
        });

        // Add a specific toast for publication status
        if (published) {
          toast({
            title: "Preset publicado!",
            description: "O preset já está disponível para os usuários.",
          });
        }
        return newPresetId || 0;
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Erro ao salvar",
        description:
          error instanceof Error
            ? error.message
            : "Ocorreu um erro inesperado. Por favor, tente novamente.",
        variant: "destructive",
      });
      return 0;
    }
  };

  return { 
    handleSubmit,
    createPresetMutation,
    updatePresetMutation,
    deletePresetMutation
  };
};
